import { useCallback } from 'react';
import { Currency } from '@/types/currency';
import { Address } from '@/types/entity/address';
import { formatPrice } from '@/lib/utils/format-price';
import { mapCountry } from '@/utils/mappers/map-country';
import { addressNameLine } from '@/utils/address/address-name-line';

const useFormat = () => {
  // EWM-525 — delegates to the single storefront formatter (en-US +
  // narrowSymbol → `$xx.xx`, per EWM-251) so PLP/PDP/cart and the
  // CurrencyHelpers-based checkout surfaces all format identically.
  const formatCurrency = useCallback((price: number, currency: Currency) => formatPrice(price, currency as string), []);

  // EWM-950 — the name line is the COMPANY only, and is omitted entirely when the
  // address has no company, so the address begins at the street line rather than
  // rendering a blank or `undefined` line. Previously interpolated `address.name`
  // unguarded, which printed the literal string "undefined" for a nameless address.
  const formatAddress = useCallback((address: Partial<Address>) => {
    const countryName = mapCountry(address.country as string)?.name || address.country;
    const company = addressNameLine(address);
    // EWM-950 — `careOf` is delivery-routing information (a dock, a
    // department), not part of the organisation's identity, so it must not
    // disappear with the company line. It hung off `company` and was dropped
    // entirely for a company-less address. Now it stands on its own line when
    // there is no company to attach it to.
    const careOf = address.careOf?.trim();
    const nameLine = company ? `${company}${careOf ? ` (c/o ${careOf})` : ''}` : careOf ? `c/o ${careOf}` : undefined;
    const streetLine = [address.streetName, address.streetNumber].filter(Boolean).join(' ');
    const cityLine = [[address.zip, address.city].filter(Boolean).join(' '), countryName].filter(Boolean).join(', ');

    return [nameLine, streetLine, cityLine]
      .map((line) => line?.replace(/[^\S\r\n]+/g, ' ').trim())
      .filter((line): line is string => !!line)
      .join('\n');
  }, []);

  const formatLocalDate = useCallback((date: Date | string) => {
    const dateToFormat = date instanceof Date ? date : new Date(date);

    const [day, month, year] = [
      dateToFormat.getDate().toString().padStart(2, '0'),
      (dateToFormat.getMonth() + 1).toString().padStart(2, '0'),
      dateToFormat.getFullYear().toString(),
    ];

    return `${day}-${month}-${year}`;
  }, []);

  const formatPosition = useCallback((position: number) => {
    let suffix = '';

    if (position % 100 >= 10 && position % 100 <= 20) {
      suffix = 'th';
    } else {
      suffix = ['st', 'nd', 'rd'][(position % 10) - 1] ?? 'th';
    }

    return `${position}${suffix}`;
  }, []);

  return { formatCurrency, formatAddress, formatLocalDate, formatPosition };
};

export default useFormat;
